import {
  getAuth,
  signInWithCustomToken,
  signOut,
  User as FirebaseUser,
  ParsedToken,
  IdTokenResult,
} from 'firebase/auth';
import app from './firebase.config';
import siteConfig from '@config';

interface Claims extends ParsedToken {
  'https://hasura.io/jwt/claims': {
    'x-hasura-default-role'?: string;
    'x-hasura-allowed-customers'?: string;
    'x-hasura-allowed-payers'?: string;
    'x-hasura-payer-child-customers'?: string;
    'x-hasura-allowed-regions'?: string;
    'x-hasura-allowed-roles'?: string;
    'x-hasura-customer-id'?: string;
    'x-hasura-user-id'?: string;
    'x-hasura-user-email'?: string;
  };
}

//Claims after they have been converted into their useable types
interface ConvertedClaims {
  'x-hasura-default-role'?: string;
  'x-hasura-allowed-customers'?: number[];
  'x-hasura-allowed-payers'?: number[];
  'x-hasura-payer-child-customers'?: number[];
  'x-hasura-allowed-regions'?: number[];
  'x-hasura-allowed-roles'?: string[];
  'x-hasura-customer-id'?: number;
  'x-hasura-user-id'?: number;
  'x-hasura-user-email'?: string;
}

export type User = {
  getToken: () => Promise<string | undefined>;
  claims: ConvertedClaims & { 'https://hasura.io/jwt/claims': Claims['https://hasura.io/jwt/claims'] };
  rooftopId?: number;
};

const auth = getAuth(app);

// Store the Firebase token received from hash authentication
let firebaseAuthToken: string | null = null;
let onAuthCallbacks: ((user: FirebaseUser | null) => void)[] = [];

export const buildConvenientGlobalUserObject = async (rooftopId?: number): Promise<User> => {
  const claims = await getClaimsFromToken();
  console.log('buildConvenientGlobalUserObject - rooftopId param:', rooftopId);
  console.log('buildConvenientGlobalUserObject - claims:', claims);
  const user: User = {
    getToken: getAccessTokenWithAutoRefresh,
    claims: {
      ...claims,
      'https://hasura.io/jwt/claims': parsedToken?.claims[siteConfig.firebaseClaimsNamespace] || claims,
    },
    rooftopId,
  };
  console.log('buildConvenientGlobalUserObject - user:', user);
  return user;
};

// Keep a reference to the parsed token for use in the buildConvenientGlobalUserObject function
let parsedToken: IdTokenResult | null = null;

export const getClaimsFromToken = async (): Promise<ConvertedClaims> => {
  console.log('getClaimsFromToken - auth.currentUser:', auth.currentUser);
  const tokenResult = await auth.currentUser?.getIdTokenResult();
  console.log('getClaimsFromToken - tokenResult:', tokenResult);
  parsedToken = tokenResult || null;

  // Check if claims are nested under namespace or at top level
  const claimsSource = parsedToken?.claims[siteConfig.firebaseClaimsNamespace] || parsedToken?.claims || {};
  const rawClaims = JSON.parse(JSON.stringify(claimsSource));

  if (rawClaims[siteConfig.firebaseClaimsAllowedRegions])
    rawClaims[siteConfig.firebaseClaimsAllowedRegions] = JSON.parse(
      getArrayFromHasuraArray(rawClaims[siteConfig.firebaseClaimsAllowedRegions])
    );

  if (rawClaims[siteConfig.firebaseClaimsPayerChildCustomers])
    rawClaims[siteConfig.firebaseClaimsPayerChildCustomers] = JSON.parse(
      getArrayFromHasuraArray(rawClaims[siteConfig.firebaseClaimsPayerChildCustomers])
    );

  if (rawClaims[siteConfig.firebaseClaimsAllowedCustomers])
    rawClaims[siteConfig.firebaseClaimsAllowedCustomers] = JSON.parse(
      getArrayFromHasuraArray(rawClaims[siteConfig.firebaseClaimsAllowedCustomers])
    );

  if (rawClaims[siteConfig.firebaseClaimsAllowedPayers])
    rawClaims[siteConfig.firebaseClaimsAllowedPayers] = JSON.parse(
      getArrayFromHasuraArray(rawClaims[siteConfig.firebaseClaimsAllowedPayers])
    );

    console.log('rawClaims', rawClaims);

  // Convert allowed roles if it's a Hasura array or ensure it's an array
  if (rawClaims[siteConfig.firebaseClaimsAllowedRoles]) {
    if (typeof rawClaims[siteConfig.firebaseClaimsAllowedRoles] === 'string') {
      // If it's a string, try to parse it as a Hasura array
      try {
        rawClaims[siteConfig.firebaseClaimsAllowedRoles] = JSON.parse(
          getArrayFromHasuraArray(rawClaims[siteConfig.firebaseClaimsAllowedRoles])
        );
      } catch (e) {
        // If parsing fails, wrap it in an array
        rawClaims[siteConfig.firebaseClaimsAllowedRoles] = [rawClaims[siteConfig.firebaseClaimsAllowedRoles]];
      }
    }
    // If it's already an array, leave it as is
  }

  if (
    rawClaims[siteConfig.firebaseClaimsCustomerId] &&
    !isNaN(Number(rawClaims[siteConfig.firebaseClaimsCustomerId]))
  ) {
    rawClaims[siteConfig.firebaseClaimsCustomerId] = parseInt(rawClaims[siteConfig.firebaseClaimsCustomerId]);
  }

  return rawClaims;
};

export const checkForPermittedRole = (user?: { claims?: ConvertedClaims }) => {
  const allowedRoles = user?.claims?.['x-hasura-allowed-roles'] || [];
  const defaultRole = user?.claims?.['x-hasura-default-role'] || ``;
  if (!allowedRoles.includes(defaultRole)) allowedRoles.push(defaultRole);
  return (
    allowedRoles.includes(`admin`) ||
    allowedRoles.includes(`dispatcher`) ||
    allowedRoles.includes(`dealer-super-admin`) ||
    allowedRoles.includes(`dealer-admin`) ||
    allowedRoles.includes(`dealer`)
  );
};

export const checkForPowerRole = (user?: { claims?: ConvertedClaims }) => {
  const allowedRoles = user?.claims?.['x-hasura-allowed-roles'] || [];
  const defaultRole = user?.claims?.['x-hasura-default-role'] || ``;
  if (!allowedRoles.includes(defaultRole)) allowedRoles.push(defaultRole);
  return (
    allowedRoles.includes(`admin`) ||
    allowedRoles.includes(`dispatcher`) ||
    allowedRoles.includes(`dealer-super-admin`) ||
    allowedRoles.includes(`dealer-admin`)
  );
};

const getArrayFromHasuraArray = (arr: string) => {
  try {
    const replacedJSON = JSON.stringify(arr).replace('{', '[').replace('}', ']');
    const parsedJSON = JSON.parse(replacedJSON);
    return parsedJSON;
  } catch (error) {
    console.log(`Failed to convert Hasura array`, { hasuraArray: arr, error });
    return;
  }
};

/**
 * Authenticate using an encrypted hash from the URL
 * This replaces the email/password login flow
 */
const loginWithHash = async (hash: string, rooftopId?: string | null): Promise<{ type: string; rooftopId?: number }> => {
  try {
    const response = await fetch('/.netlify/functions/authenticate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ hash, rooftopId }),
    });

    if (!response.ok) {
      throw new Error(`Authentication failed: ${response.statusText}`);
    }

    const result = await response.json();

    // Sign in with the custom Firebase token
    await signInWithCustomToken(auth, result.token);

    // Store the token
    firebaseAuthToken = result.token;

    // For dealer hashes the decoded value is the rooftop ID.
    const extractedRooftopId =
      typeof result.value === 'number' ? result.value : undefined;

    return {
      type: result.type,
      rooftopId: extractedRooftopId,
    };
  } catch (error) {
    console.error('Hash authentication failed:', error);
    throw error;
  }
};

const logout = () => {
  firebaseAuthToken = null;
  return signOut(auth);
};

const onAuthStateChanged = (callback: (user: FirebaseUser | null) => void) => {
  onAuthCallbacks.push(callback);

  // Return Firebase's auth state listener
  return auth.onAuthStateChanged(callback);
};

const getAccessTokenWithAutoRefresh = async (): Promise<string | undefined> => {
  try {
    let userId;
    let tokenIssued;
    let firebaseTokenResult = null;
    if (auth && auth.currentUser) {
      firebaseTokenResult = await auth.currentUser.getIdTokenResult();
    }
    return firebaseTokenResult?.token;
  } catch (err) {
    console.log('Error getting most up-to-date token', err);
    return;
  }
};

const getAuthStatus = async () => {
  if (auth && auth.currentUser) {
    return true;
  } else return false;
};

const getAccessToken = async () => {
  try {
    const token = await auth.currentUser?.getIdToken();
    return token;
  } catch (err) {
    console.log('Error getting token', err);
    return;
  }
};

export type TokenInfo =
  | { available: false; error?: string }
  | {
      available: true;
      iat: number;
      exp: number;
      tokenAgeSec: number;
      tokenExpiresInSec: number;
      tokenExpired: boolean;
    };

/**
 * Returns parsed expiration metadata for the current Firebase ID token.
 * Calls getIdTokenResult() which auto-refreshes if the cached token has
 * expired, so a small tokenAgeSec paired with a downstream JWTExpired
 * error indicates the prior token expired mid-flow.
 */
export const getTokenInfo = async (): Promise<TokenInfo> => {
  try {
    if (!auth?.currentUser) return { available: false };
    const result = await auth.currentUser.getIdTokenResult();
    if (!result) return { available: false };
    const exp = Math.floor(new Date(result.expirationTime).getTime() / 1000);
    const iat = Math.floor(new Date(result.issuedAtTime).getTime() / 1000);
    const now = Math.floor(Date.now() / 1000);
    return {
      available: true,
      iat,
      exp,
      tokenAgeSec: now - iat,
      tokenExpiresInSec: exp - now,
      tokenExpired: exp <= now,
    };
  } catch (err: any) {
    return { available: false, error: err?.message };
  }
};

const authService = {
  loginWithHash,
  logout,
  onAuthStateChanged,
  getAccessTokenWithAutoRefresh,
  getAccessToken,
  getAuthStatus,
};

export { auth, authService };

export default authService;
